import React, { useMemo, useState } from 'react'
import { ActivityIndicator, FlatList, Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useRoute, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Screen, Header, Text, SearchBar, BackButtonWeb, Icon } from '@components'
import dayjs, { Dayjs } from 'dayjs'
import { birthDayOf, Scope } from '@utils'
import { Birthday, CalendarEvent, usePaginateBirthdays, useScreen } from '@hooks';
import ContactsFilter from '../components/ContactsFilter'
import { computeUpcomingBirthdays } from './upcomingBirthdays'
import { useSendBirthdayWish } from './useSendBirthdayWish'
import { ScrollView } from 'react-native'
import CalendarComponent from '../calendar/components/CalendarComponent'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

// One request for the strip and the calendar dots: every birthday of the group.
const ALL_BIRTHDAYS_LIMIT = 1000

export const Birthdays: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const {isDesktop, isWeb, isMobile} = useScreen();
  const styles = getStyles(theme)
  const navigation = useNavigation()
  const route = useRoute()
  const params = route.params as {contactId?: string}
  const contactId = params?.contactId

  const [searchText, setSearchText] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [scopes, setScopes] = useState<Scope[]>([Scope.PRIVATE]);

  const scopeFilter = scopes.includes(Scope.PRIVATE)
    ? Scope.PRIVATE
    : (scopes.includes(Scope.PROFESSIONAL) ? Scope.PROFESSIONAL : undefined)

  const { flatData: birthdays, isFetching, fetchNextPage, hasNextPage } = usePaginateBirthdays({
    search: searchText,
    birthday: selectedDate?.format('YYYY-MM-DD'),
    scope: scopeFilter,
    // The list starts today; the birthdays already passed this year come last.
    from: dayjs().format('MM-DD'),
  })

  // For real contacts the /birthdays `id` IS the related accountId (ghosts get
  // their ghost-profile id and can't receive messages — button hidden for them).
  const sendMessage = useSendBirthdayWish()

  // Unfiltered list (no search / no day) used for the "upcoming" strip and the
  // calendar dots, so they stay visible and stable while the user searches or
  // picks a day.
  const { flatData: allBirthdays } = usePaginateBirthdays({ scope: scopeFilter, limit: ALL_BIRTHDAYS_LIMIT })

  // Next 8 birthdays by their next occurrence from today (ignores the year).
  const upcomingBirthdays = useMemo<Birthday[]>(
    () => computeUpcomingBirthdays(allBirthdays),
    [allBirthdays],
  )

  const renderUpcoming = () => {
    if (upcomingBirthdays.length === 0) return null
    return (
      <View style={styles.upcomingSection}>
        <Text type="bodyBold" color={theme.colors.primary} style={styles.upcomingTitle}>
          {t('birthdaysScreen.upcoming')}
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.upcomingRow}>
          {upcomingBirthdays.map((b) => (
            <TouchableOpacity
              key={b.id}
              testID={`upcoming-birthday-${b.id}`}
              style={styles.upcomingCard}
              onPress={() => (navigation as any).navigate('Profile', {
                screen: 'DetailsGhostProfile',
                params: { profileId: b.id },
              })}
            >
              <Image source={b.avatarUrl ? { uri: b.avatarUrl } : defaultImage} style={styles.upcomingAvatar} />
              <Text type="bodyExtraSmall" numberOfLines={1} style={styles.upcomingName}>
                {b.fullName}
              </Text>
              <Text type="microText" color={theme.colors.greyBlue}>
                {dayjs(birthDayOf(b.birthDate), 'YYYY-MM-DD').format('DD MMM')}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    )
  }

  const birthdayEvents = useMemo<CalendarEvent[]>(() => {
    return allBirthdays.map((b) => ({
      id: b.id,
      title: b.fullName,
      description: '',
      startDateTime: b.birthDate,
      endDateTime: null,
      allDay: true,
      rrule: null,
      location: { place: '' },
      type: 'private' as any,
      scope: b.scope?.toUpperCase() || Scope.PRIVATE,
      participantAccountIds: [],
      hasReminder: false,
      descriptionAsReminder: false,
      createdAt: b.createdAt,
      updatedAt: b.updatedAt,
    }))
  }, [allBirthdays])
  
  const renderEvent = ({item}: {item: Birthday}) => {
    return (
      <TouchableOpacity style={{ marginTop: 10,  borderRadius: 5, flexDirection: 'row', justifyContent: 'space-between', padding: theme.spacing.md, borderBottomWidth: 1, borderBottomColor: theme.colors.grey20 }} onPress={() => (navigation as any).navigate('Profile', {
        screen: 'DetailsGhostProfile',
        params: { profileId: item.id }
      })}>
        <View style={{flex: 1, flexDirection: 'row', gap: 10, alignItems: 'center'}}>
          <Image
            source={item.avatarUrl ? { uri: item.avatarUrl } : defaultImage}
            style={styles.avatar}
          />
          <View>
            <Text type='body'>{item.fullName}</Text>
            <Text type='bodySmall' color={theme.colors.greyBlue} numberOfLines={1}>{item.hashtags?.join(' ')}</Text>
          </View>
        </View>
        <View style={styles.rightSection}>
          <TouchableOpacity
            style={item.isGhost && styles.messageButtonHidden}
            onPress={() => sendMessage(item)}
            disabled={item.isGhost}
            activeOpacity={0.7}
            testID={`birthday-message-${item.id}`}
          >
            <Icon name="messageCircle" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          <Text type='bodySmall' color={theme.colors.greyBlue}>{dayjs(birthDayOf(item.birthDate), 'YYYY-MM-DD').format('DD/MM/YYYY')}</Text>
        </View>
      </TouchableOpacity>
    )
  }

  return (
    <Screen>
      {isWeb && contactId && <BackButtonWeb title={t('form.backToProfile')} onPress={() => navigation.goBack()} />}
      <Header
        title={t('menuItems.birthdays')}
        iconLeft={isWeb ? undefined : "arrowBack"}
        onPressLeft={() => navigation.goBack()}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={isDesktop && styles.row} scrollEnabled={!isDesktop}>
        <View>
          <View style={isMobile? styles.searchContainerMobile : styles.searchContainerWeb}>
            <View style={styles.searchContainer}>
              <SearchBar value={searchText} onChangeText={setSearchText} hasMaxWidth={!isMobile} />
            </View>
            <View style={styles.filtersContainer}>
              <ContactsFilter scope={scopes} onScopeChange={setScopes} noGhost />
            </View>
          </View>
          {/* Calendar */}
          <View style={[styles.calendarContainer, isDesktop && styles.calendarContainerDesktop]}>
            <CalendarComponent yearly events={birthdayEvents} allowDeselect onDayChange={(day: Dayjs | null) => setSelectedDate(day)} />
          </View>
        </View>

        <FlatList
          data={birthdays}
          renderItem={renderEvent}
          ListHeaderComponent={renderUpcoming}
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          onEndReached={() => {
            if (hasNextPage && !isFetching && fetchNextPage) {
              fetchNextPage()
            }
          }}
          onEndReachedThreshold={0.5} 
          ListFooterComponent={isFetching ? <ActivityIndicator size="large" color={theme.colors.primary} /> : null}
        />
      </ScrollView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    avatar: {
      width: 50,
      height: 50,
      borderRadius: 25,
      backgroundColor: theme.colors.background,
    },
    searchContainerMobile: {
      marginBottom: theme.spacing.md,
    },
    searchContainerWeb: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.lg,
    },
    searchContainer: {
      flex: 1,
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
    },
    filtersContainer: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
    },
    scrollView: {
      flex: 1,
    },
    row: {
      height: '100%',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: theme.spacing.lg,
    },
    content: {
      flex: 1,
      height: '100%',
      flexGrow: 1,
      marginRight: theme.spacing.lg,
    },
    contentContainer: {
      paddingBottom: theme.spacing.lg,
    },
    upcomingSection: {
      marginBottom: theme.spacing.md,
      paddingTop: theme.spacing.sm,
    },
    upcomingTitle: {
      marginBottom: theme.spacing.sm,
    },
    upcomingRow: {
      gap: theme.spacing.md,
      paddingRight: theme.spacing.md,
    },
    upcomingCard: {
      alignItems: 'center',
      width: 72,
      gap: theme.spacing.xxs,
    },
    upcomingAvatar: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: theme.colors.background,
      borderWidth: 2,
      borderColor: theme.colors.primary + '40',
    },
    upcomingName: {
      textAlign: 'center',
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      alignSelf: 'center',
    },
    // Mirrors ContactsList: ghosts have no account to message, hide the button.
    messageButtonHidden: {
      opacity: 0,
    },
    calendarContainer: {
      backgroundColor: '#ffffff',
      margin: 20,
      borderRadius: 12,
      padding: 10,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 3.84,
      elevation: 5,
    },
    // The column is as wide as the search + Privé/Pro row: stretched to it,
    // the card had blank bands around the calendar. Hug it, as on Rendez-vous.
    calendarContainerDesktop: {
      alignSelf: 'flex-start',
    },
  })
